import React, { useState, useEffect } from 'react';
import { uploadAndExtractFloorplanPair } from '../services/floorplanExtractionService';
import '../styles/MergeAnnotatedFloorplanModal.css';

/**
 * Admin-only modal: upload a NON-ANNOTATED + ANNOTATED floor plan pair and
 * extract a structure in CV "pair mode" — geometry from the non-annotated
 * image, scale calibrated from the annotated image's dimension rulers.
 *
 * Props:
 *   isOpen, onClose
 *   projectId   — project to create the structure in
 *   onComplete(result) — called with { structureId, roomIds, dimensions }
 */
function MergeAnnotatedFloorplanModal({
  isOpen,
  onClose,
  projectId,
  onComplete,
}) {
  const [planFile, setPlanFile] = useState(null);
  const [annotatedFile, setAnnotatedFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setPlanFile(null);
      setAnnotatedFile(null);
      setBusy(false);
      setStatus(null);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleExtract = async () => {
    if (!planFile || !annotatedFile) return;
    setBusy(true);
    setError(null);
    try {
      const res = await uploadAndExtractFloorplanPair(
        planFile,
        annotatedFile,
        projectId,
        (_stage, msg) => setStatus(msg)
      );
      // Navigate straight to the merged structure — no confirmation step.
      // onComplete unmounts this modal, so leave `busy` set rather than
      // clearing it on a now-gone component.
      onComplete(res);
    } catch (err) {
      console.error('Pair extraction failed:', err);
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div className="merge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merge-modal-header">
          <h2>Upload paired floor plans</h2>
          <button
            className="merge-modal-close"
            onClick={onClose}
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div className="merge-modal-body">
          <p className="merge-modal-hint">
            Geometry is extracted from the non-annotated plan; scale is read
            from the annotated plan's dimension rulers.
          </p>

          <FilePicker
            label="Non-annotated plan (geometry)"
            file={planFile}
            onPick={setPlanFile}
            disabled={busy}
          />
          <FilePicker
            label="Annotated plan (dimensions)"
            file={annotatedFile}
            onPick={setAnnotatedFile}
            disabled={busy}
          />

          {status && busy && <p className="merge-modal-status">{status}</p>}
          {error && <p className="merge-modal-error">Error: {error}</p>}
        </div>

        <div className="merge-modal-footer">
          <button
            className="merge-modal-cancel"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="merge-modal-primary"
            onClick={handleExtract}
            disabled={busy || !planFile || !annotatedFile}
          >
            {busy ? 'Uploading…' : 'Upload paired floor plans'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilePicker({ label, file, onPick, disabled }) {
  return (
    <label className="merge-file-picker">
      <span className="merge-file-label">{label}</span>
      <span className="merge-file-name">
        {file ? file.name : 'No file chosen'}
      </span>
      <input
        type="file"
        accept="image/*"
        disabled={disabled}
        onChange={(e) => onPick(e.target.files?.[0] || null)}
      />
    </label>
  );
}

export default MergeAnnotatedFloorplanModal;
